import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  Card,
  ContentBox,
  ContentPage,
  ExportIcon,
  NoResults,
  Pagination,
  Skeleton,
  TableFilters,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import useListFilters from "@hooks/useListFilters";
import { documentMeta, downloadDocument } from "@helpers/documentFile";
import { escapeHtml, formatDateTime, usePageHeader } from "@components/ow";

// Documentos que a empresa ativa compartilhou com o prestador (modelo Lista em
// cartões do DS OneWork). O cartão inteiro baixa o documento.

// Múltiplo das 3 colunas da grade; a paginação só aparece com mais de uma página
const PAGE_SIZE = 30;

const BREADCRUMB = [{ page: "Documentos", route: "/e/documents" }];

const EMPTY = { list: [], totalRegisters: 0, totalPages: 1 };

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

// Nome em até 2 linhas; o inteiro fica no title
const Name = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.N100};
  color: ${({ theme }) => theme.colors.ACTION};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Pager = styled.div`
  margin-top: 20px;
`;

function DocumentCardsSkeleton() {
  return (
    <Grid aria-busy="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <Skeleton $width="65%" $height="20px" $noFlex />
          <Skeleton $width="35%" $height="14px" $noFlex />
          <Skeleton $width="50%" $height="14px" $noFlex />
          <Skeleton $width="25%" $height="14px" $noFlex />
        </Card>
      ))}
    </Grid>
  );
}

export default function DocumentsEmployee() {
  const documentsService = useService("documents");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  // Só a resposta da última requisição vale (busca e troca de página seguidas)
  const lastRequest = useRef(0);

  const { storeFilters, filters, filter, setOpenFilter, handleSetFilters, handleClearFilters, hasFilter } = useListFilters(
    {},
    setPagination
  );

  usePageHeader({ title: "Documentos", breadcrumb: BREADCRUMB });

  const searchTerm = (filters.search || "").trim();

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, searchTerm]);

  async function fetchDocuments() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      // Sem scope, o back devolve só os compartilhados com o prestador, na empresa ativa
      const params = [`page=${pagination.pageIndex + 1}`, `limit=${PAGE_SIZE}`];
      if (searchTerm) params.push(`filename=${encodeURIComponent(searchTerm)}`);
      const result = await documentsService.list(params.join("&"));
      if (request !== lastRequest.current) return;
      if (!result?.data?.list) throw new Error(typeof result === "string" ? result : "Erro ao carregar documentos");
      setData({
        list: result.data.list,
        totalRegisters: result.data.totalRegisters ?? result.data.list.length,
        totalPages: result.data.totalPages || 1,
      });
    } catch (error) {
      if (request !== lastRequest.current) return;
      toast.error(error?.response?.data?.msg || error?.message || "Erro ao carregar documentos");
      setData(EMPTY);
    }
    setLoading(false);
  }

  let content;
  if (loading) {
    content = <DocumentCardsSkeleton />;
  } else if (data.list.length === 0) {
    content = searchTerm ? (
      <NoResults notResultText="Nenhum documento encontrado" includeText="Limpar busca" includeAction={handleClearFilters} />
    ) : (
      <NoResults notResultText="Nenhum documento por aqui" />
    );
  } else {
    content = (
      <>
        <Grid>
          {data.list.map((doc) => {
            const meta = documentMeta(doc);
            return (
              <Card key={doc.id} $action={() => downloadDocument(documentsService, doc)} title={`Baixar ${doc.filename || "documento"}`}>
                <Name>
                  <Text $text={escapeHtml(doc.filename || "Documento")} $variant="h4_bold" $color="N900" />
                </Name>
                <Details>
                  {meta ? <Text $text={meta} $variant="caption_medium" $color="N600" /> : null}
                  {doc.created_at ? (
                    <Text $text={`Enviado em ${formatDateTime(doc.created_at)}`} $variant="caption_medium" $color="N600" />
                  ) : null}
                </Details>
                <Foot>
                  <Text $text="Baixar" $variant="paragraph_small_bold" $color="ACTION" />
                  <ExportIcon />
                </Foot>
              </Card>
            );
          })}
        </Grid>
        {data.totalPages > 1 ? (
          <Pager>
            <Pagination
              $pageIndex={pagination.pageIndex}
              $pageCount={data.totalPages}
              $totalRecords={data.totalRegisters}
              $pageSize={PAGE_SIZE}
              onChangePage={(pageIndex) => setPagination((prev) => ({ ...prev, pageIndex }))}
            />
          </Pager>
        ) : null}
      </>
    );
  }

  return (
    <ContentPage>
      <ContentBox>
        <TableFilters
          storeFilters={storeFilters}
          searchTerm="search"
          searchPlaceholder="Buscar pelo nome do documento"
          filter={filter}
          handleSetFilters={handleSetFilters}
          handleClearFilters={handleClearFilters}
          setStatePagination={setPagination}
          openFilter={() => setOpenFilter((prev) => !prev)}
          disableFilter
          disableExport
          hasFilter={hasFilter}
        />
        {content}
      </ContentBox>
    </ContentPage>
  );
}
